import axios, { AxiosInstance } from 'axios';
import { 
  LoginForm, 
  AuthResponse, 
  User, 
  ChatRequest, 
  ChatResponse, 
  Conversation, 
  Message,
  ForgotPasswordForm,
  ResetPasswordForm,
  UpdateProfileForm
} from '../types';

// Create axios instance - Updated for Heroku deployment
const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://sergic-backend-7526fb1dc158.herokuapp.com/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  // Login user
  login: async (formData: LoginForm): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', formData);
    return response.data;
  },

  // Get current user
  getCurrentUser: async (): Promise<{ user: User }> => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  // Forgot password
  forgotPassword: async (formData: ForgotPasswordForm): Promise<{ message: string }> => {
    const response = await api.post('/auth/forgot-password', formData);
    return response.data;
  },

  // Reset password
  resetPassword: async (formData: ResetPasswordForm): Promise<{ message: string }> => {
    const response = await api.post('/auth/reset-password', formData);
    return response.data;
  },

  // Update profile
  updateProfile: async (formData: UpdateProfileForm): Promise<{ message: string; user: User }> => {
    const response = await api.put('/auth/profile', formData);
    return response.data;
  },

  // Change password
  changePassword: async (formData: { currentPassword: string; newPassword: string }): Promise<{ message: string }> => {
    const response = await api.put('/auth/change-password', formData);
    return response.data;
  },

  // Delete account
  deleteAccount: async (): Promise<{ message: string }> => {
    const response = await api.delete('/auth/account');
    return response.data;
  },
};

// Chat API
export const chatAPI = {
  // Create new conversation
  createConversation: async (): Promise<{ conversation: Conversation }> => {
    const response = await api.post('/chat/conversation');
    return response.data;
  },

  // Send message
  sendMessage: async (request: ChatRequest): Promise<ChatResponse> => {
    const response = await api.post('/chat/query', request);
    return response.data;
  },

  // Get conversations
  getConversations: async (): Promise<Conversation[]> => {
    const response = await api.get('/chat/history');
    return response.data.conversations;
  },

  // Get messages for a conversation
  getMessages: async (conversationId: number): Promise<Message[]> => {
    const response = await api.get(`/chat/history/${conversationId}`);
    return response.data.messages;
  },

  // Delete conversation
  deleteConversation: async (conversationId: number): Promise<void> => {
    await api.delete(`/chat/history/${conversationId}`);
  },

  // Update conversation title
  updateConversationTitle: async (conversationId: number, title: string): Promise<void> => {
    await api.put(`/chat/history/${conversationId}/title`, { title });
  },

  // Start async chat request
  startAsyncChat: async (data: { message: string; conversationId?: number }) => {
    const response = await api.post('/chat/async/start', data);
    return response.data;
  },

  // Get async request status
  getAsyncStatus: async (requestId: string) => {
    const response = await api.get(`/chat/async/status/${requestId}`);
    return response.data;
  },
};

export default api;
